/*
 * @Copyright 2024-2026. Institute for Future Intelligence, Inc.
 *
 * The angle-bending potential is 0.5*strength*(theta-angle)^2
 *
 * See AMBER force field: http://en.wikipedia.org/wiki/AMBER
 */

import { Atom } from './Atom.ts';
import { Vector3 } from 'three';
import { GF_CONVERSION_CONSTANT } from './physicalConstants.ts';

export class AngularBond {
  static readonly DEFAULT_STRENGTH = 10;

  /* return the angle a1-a2-a3 (a2 is in the middle) */
  static getAngle(a1: Atom, a2: Atom, a3: Atom) {
    return AngularBond.getAngleFromPositions(a1.position, a2.position, a3.position);
  }

  /* return the angle p1-p2-p3 (p2 is in the middle) */
  static getAngleFromPositions(p1: Vector3, p2: Vector3, p3: Vector3) {
    const x21 = p2.x - p1.x;
    const y21 = p2.y - p1.y;
    const z21 = p2.z - p1.z;
    const x23 = p2.x - p3.x;
    const y23 = p2.y - p3.y;
    const z23 = p2.z - p3.z;
    const xx = y21 * z23 - z21 * y23;
    const yy = z21 * x23 - x21 * z23;
    const zz = x21 * y23 - y21 * x23;
    return Math.abs(Math.atan2(Math.sqrt(xx * xx + yy * yy + zz * zz), x21 * x23 + y21 * y23 + z21 * z23));
  }

  atom1: Atom; // end atom
  atom2: Atom; // middle atom (shared by two flanking radial bonds)
  atom3: Atom; // end atom
  strength: number;
  angle: number; // equilibrium angle when the angular force is zero

  constructor(atom1: Atom, atom2: Atom, atom3: Atom, angle?: number) {
    this.atom1 = atom1;
    this.atom2 = atom2;
    this.atom3 = atom3;
    this.strength = AngularBond.DEFAULT_STRENGTH;
    this.angle = angle !== undefined ? angle : AngularBond.getAngle(atom1, atom2, atom3);
  }

  containsAtom(atom: Atom): boolean {
    return this.atom1 === atom || this.atom2 === atom || this.atom3 === atom;
  }

  getCurrentAngle(): number {
    return AngularBond.getAngle(this.atom1, this.atom2, this.atom3);
  }

  /**
   * Energy-only: V(θ) = 0.5·strength·(θ − angle)². Used by compute() to
   * drive the finite-difference gradient. No state mutation; safe to
   * call from inside the FD perturbation loop.
   */
  private computeEnergy(): number {
    const theta = AngularBond.getAngleFromPositions(this.atom1.position, this.atom2.position, this.atom3.position);
    if (!Number.isFinite(theta)) return 0;
    const delta = theta - this.angle;
    return 0.5 * this.strength * delta * delta;
  }

  /**
   * Compute angular force + energy on all three atoms.
   *
   * Strategy: V is the harmonic angle potential
   *   V(θ) = 0.5·strength·(θ − angle)²
   * with θ the angle at atom2 between (atom2→atom1) and (atom2→atom3).
   * Forces are −∇V computed by central-difference numerical gradient,
   * matching the pattern in TorsionalBond.compute().
   *
   * Why finite difference vs the closed-form gradient: the analytic
   * formula has a 1/sin(θ) factor that explodes near 180° (and 0°). The
   * legacy implementation clamped sin to MIN_SIN_THETA to avoid the
   * singularity, which kept the FORCE bounded but no longer matched
   * −∇V at those angles. A numerical-gradient verifier (see
   * angularBond.test.ts) showed |F − (−∇V)| jumping from 1e-9 to
   * ~15 eV/Å the moment the clamp fired. That mismatch leaked energy
   * into NVE — a polymer chain warmed from 300 K to 840 K over a few
   * ps because some bond angle transiently visited the clamp band
   * during high-T fluctuation, and each visit injected ~0.3 eV of
   * spurious energy. FD has no such pathology: V is smooth in all atom
   * coordinates including the 180° degenerate plane, so its
   * central-difference gradient is well-defined everywhere.
   *
   * Cost: 6 V-evaluations × 3 atoms × 3 axes = 18 evaluations per bond
   * per step. Each V eval is one atan2 + a few vector ops, so the
   * overhead vs the analytic version is modest; the bigger MD loops
   * (NB pair, LJ) dominate per-step time even for thousand-bond chains.
   *
   * Acceleration scaling matches the radial / torsional bond convention:
   * raw force (eV/Å) is multiplied by GF_CONVERSION_CONSTANT / mass
   * before being added to atom.force, which the integrator treats as
   * acceleration.
   */
  compute(): number {
    if (this.atom1.fixed && this.atom2.fixed && this.atom3.fixed) return 0;
    if (!this.atom1.force || !this.atom2.force || !this.atom3.force) return 0;

    const energy = this.computeEnergy();
    if (energy === 0 && this.strength === 0) return 0;

    // Central-difference numerical gradient. Forward difference was
    // attempted as an optimization, but at near-180° equilibria (sp1
    // atoms have angle_eq = 180°) the second derivative is large and
    // forward's O(h·V'') error becomes ≳ 1 eV/Å — visible in the test
    // suite and a non-negligible perturbation to NVE. Central preserves
    // O(h²) accuracy at the cost of 2× the V evaluations, but with at
    // most a few hundred angular bonds in a polymer chain the absolute
    // budget is still small compared to torsion (which keeps forward).
    const h = 1e-5;
    const atoms = [this.atom1, this.atom2, this.atom3];
    const forces = [this.atom1.force, this.atom2.force, this.atom3.force];
    const axes = ['x', 'y', 'z'] as const;

    for (let i = 0; i < 3; i++) {
      if (atoms[i].fixed) continue;
      const invMass = GF_CONVERSION_CONSTANT / atoms[i].mass;
      for (const axis of axes) {
        const p = atoms[i].position;
        const original = p[axis];
        p[axis] = original + h;
        const vPlus = this.computeEnergy();
        p[axis] = original - h;
        const vMinus = this.computeEnergy();
        p[axis] = original;
        const fComponent = -(vPlus - vMinus) / (2 * h);
        forces[i][axis] += fComponent * invMass;
      }
    }

    return energy;
  }
}
